import {
  ArrowLeftOutlined,
  LeftOutlined,
  RightOutlined,
  WarningOutlined
} from "@ant-design/icons"
import { Button, Divider, Flex, Skeleton, Tag, Tooltip, Typography } from "antd"
import React, { useEffect, useMemo } from "react"
import { useTranslation } from "react-i18next"
import { Link, useLocation, useNavigate } from "react-router-dom"

import Icon from "../../../../Icon"
import { RecordRef } from "../../../../components/RecordRef.tsx"
import { RoutesPaths } from "../../../../constants/routes.ts"
import { useAppDispatch } from "../../../../hooks/use-app-dispatch.ts"
import { useAppSelector } from "../../../../hooks/use-app-selector.ts"
import { semantic } from "../../../../components/widgets/tokens.ts"
import {
  accountingFormat,
  budgetFormat
} from "../../../../utils/number/budget-format.ts"
import { getEntityName } from "../../../../utils/user/get-entity-name"
import { loadProjectsFilterOptions } from "../../../Projects/redux/projects.thunks.ts"
import {
  ProjectStatusSelector,
  type StatusChangeOptions
} from "../../components/ProjectStatusSelector.tsx"
import { ProjectStatusTag } from "../../components/ProjectStatusTag.tsx"
import { sendStatusChange } from "../../helpers/send-status-change.ts"
import {
  AMOUNT_FIELD,
  selectContributingMilestones
} from "./sections/ProjectViewFinance/financial-alerts-milestones.ts"
import {
  loadFinancialAlerts,
  loadFinancialSnapshot
} from "../../redux/project-finance/project-finance.thunks.ts"
import { fetchAlertContributingMilestones } from "../../redux/project-milestones/project-milestones.thunks.ts"
import { projectSetProp } from "../../redux/project.slice.ts"
import {
  refreshProjectLogHistory,
  updateProject
} from "../../redux/project.thunks.ts"
import type { ContractBreakdownSide } from "../../types/project-finance.types.tsx"
import { type Project } from "../../types/project.types.ts"
import type { ProjectSectionKeys } from "./ProjectViewSectionsTabs.tsx"
import { typeLabel } from "../../helpers/repeated-project-type.ts"
import { RecordHeaderSendActions } from "../../../Communication/record/RecordCommunicationEntries.tsx"
import {
  ProjectStatusHistory,
  ProjectStatusSuggestions
} from "./ProjectStatusAutomation.tsx"
import { AiPlanButton } from "../../../AiCenter/sections/CustomerSowReceivables/wizard/RecordPlans.tsx"

type ContractRowKey = "sales_price" | "total_expenses"

type AlertRow = {
  severity: "warning" | "critical"
  message: string
  rule: string
  basis: "planned" | "actual"
}

// Which contract figure each rule speaks about. Both bases of a rule point at
// the same row — the planned basis is preferred as the one to explain in the
// tooltip, since that's the figure a PM is about to act on, but the highest
// severity across either basis always wins.
const ALERT_TARGET: Record<string, ContractRowKey> = {
  receivables_mismatch: "sales_price",
  contract_value_exceeded: "sales_price",
  payables_mismatch: "total_expenses",
  contract_expenses_exceeded: "total_expenses"
}

const RULE_TITLE: Record<string, string> = {
  contract_value_exceeded: "Contract Value Exceeded",
  contract_expenses_exceeded: "Contract Expenses Exceeded",
  receivables_mismatch: "Contract Value Mismatch",
  payables_mismatch: "Contract Expenses Mismatch"
}

const ROW_COPY: Record<
  ContractRowKey,
  { contractLabel: string; plannedLabel: string; actualLabel: string }
> = {
  sales_price: {
    contractLabel: "Contract Value",
    plannedLabel: "Planned Receivables",
    actualLabel: "Actual Receivables"
  },
  total_expenses: {
    contractLabel: "Contract Expenses",
    plannedLabel: "Planned Payables",
    actualLabel: "Actual Payables"
  }
}

// A hand edit moves the contract with no CR behind it, so whether the headline
// is still the baseline is a question about the two figures, not the CR total.
const contractCaption = (
  original: number,
  current: number,
  changeRequests: number
) => {
  if (original === current) {
    return "Original contract"
  }
  return changeRequests
    ? `Original ${budgetFormat(original)} · CR +${budgetFormat(changeRequests)}`
    : `Original ${budgetFormat(original)}`
}

const { Title, Text } = Typography

const NO_FIGURE = "—"

const marginAtTwoDecimals = (value: unknown): number | null => {
  if (value === null || value === undefined || value === "") return null
  const percent = Number(value)
  if (!Number.isFinite(percent)) return null
  // Halves away from zero; the epsilon keeps 45.505 from reading 45.50499...
  return (Math.sign(percent) * Math.round(Math.abs(percent) * 100 + 1e-7)) / 100
}

const snapshots: Record<ProjectSectionKeys, boolean> = {
  milestones: true,
  finance: true,
  overview: false,
  invoices: true,
  tasks: true,
  files: false,
  commissions: true,
  // A conversation fills the pane; a financial snapshot above it would only
  // push the composer off the screen.
  messages: false,
  communication: false,
  logs: false
  // activity: false
}

interface ProjectViewHeaderProps {
  activeTab: ProjectSectionKeys | null
  project: Project
  showSnapshotToggle: boolean
  canEditProject: boolean
  canChangeStatus: boolean
  canClose: boolean
  canViewLogs: boolean
  showFinanceSnapshot: boolean
}

export function ProjectViewHeader({
  activeTab,
  project,
  showSnapshotToggle,
  canEditProject,
  canChangeStatus,
  canClose,
  canViewLogs,
  showFinanceSnapshot
}: ProjectViewHeaderProps) {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const location = useLocation()

  // Same idiom as `LeadWorkspacePage`'s `back_to_list` (`location.key` is
  // react-router's own per-entry id, "default" only for the entry the app
  // was loaded on) — browser history, not a fixed destination, so a project
  // opened from the home page's Location tile returns there.
  const handleBack = () => {
    if (location.key !== "default") {
      navigate(-1)
    } else {
      navigate(RoutesPaths.project.grid)
    }
  }

  const showSnapshot = useAppSelector((state) => state?.project?.showSnapshot)

  const options = useAppSelector((state) => state?.projects?.filterOptions)
  const fetchingOptions = useAppSelector(
    (state) =>
      !!state?.projects?.fetchingFilterOptions &&
      !state?.projects?.filterOptions?.project_status?.length
  )

  const contractBreakdown = useAppSelector(
    (state) => state?.project?.financialTransactionSnapshot?.contract_breakdown
  )
  const salesPriceSnapshot = useAppSelector(
    (state) => state?.project?.financialTransactionSnapshot?.sales_price
  )
  const totalExpensesSnapshot = useAppSelector(
    (state) => state?.project?.financialTransactionSnapshot?.total_expenses
  )
  const expectedGrossProfit = useAppSelector(
    (state) =>
      state?.project?.financialTransactionSnapshot?.expected_gross_profit
  )
  const expectedMarginPercent = useAppSelector(
    (state) =>
      state?.project?.financialTransactionSnapshot?.expected_margin_percent
  )
  const expectedMarginSource = useAppSelector(
    (state) =>
      state?.project?.financialTransactionSnapshot?.expected_margin_source
  )
  const alerts = useAppSelector((state) => state?.project?.financialAlerts)
  const milestones = useAppSelector(
    (state) => state?.project?.alertContributingMilestones
  )

  // The Financial Alerts sidebar section is a notification list the user can
  // clear; this row reflects the CURRENT financial condition and is
  // unaffected by that — it only changes when the underlying numbers do.
  const alertByRow = useMemo(() => {
    const byRow: Partial<Record<ContractRowKey, AlertRow[]>> = {}

    for (const alert of alerts?.alerts ?? []) {
      const key = ALERT_TARGET[alert.rule]
      if (!key) continue
      if (!byRow[key]) byRow[key] = []
      byRow[key].push({
        severity: alert.severity === "critical" ? "critical" : "warning",
        message: alert.message,
        rule: alert.rule,
        basis: alert.basis
      })
    }

    const rows: Partial<Record<ContractRowKey, AlertRow>> = {}
    for (const key of Object.keys(byRow) as ContractRowKey[]) {
      const candidates = byRow[key] as AlertRow[]
      const severity = candidates.some((a) => a.severity === "critical")
        ? "critical"
        : "warning"
      const primary =
        candidates.find((a) => a.basis === "planned") ?? candidates[0]
      rows[key] = { ...primary, severity }
    }

    return rows
  }, [alerts])

  // The count of milestones explaining an overage, shown in the header
  // tooltip — same source the sidebar's Financial Alerts section reads, kept
  // independent so the icon still has data even if that sidebar is collapsed.
  const contributingCountByRow = useMemo(() => {
    const counts: Partial<Record<ContractRowKey, number>> = {}

    for (const key of Object.keys(alertByRow) as ContractRowKey[]) {
      const alert = alertByRow[key]
      if (!alert) continue

      const amountField = AMOUNT_FIELD[`${alert.rule}:${alert.basis}`]
      if (!amountField) continue

      const figure = Number(
        (key === "total_expenses"
          ? totalExpensesSnapshot?.[alert.basis]
          : salesPriceSnapshot?.[alert.basis]) ?? 0
      )
      const contractValue = Number(
        (key === "total_expenses"
          ? contractBreakdown?.expenses?.current
          : contractBreakdown?.receivable?.current) ?? 0
      )

      counts[key] = selectContributingMilestones(
        milestones,
        amountField,
        figure - contractValue
      ).length
    }

    return counts
  }, [
    alertByRow,
    salesPriceSnapshot,
    totalExpensesSnapshot,
    contractBreakdown,
    milestones
  ])

  useEffect(() => {
    if (showFinanceSnapshot && project?.id) {
      dispatch(loadFinancialSnapshot(project.id))
      dispatch(loadFinancialAlerts(project.id))
    }
  }, [dispatch, showFinanceSnapshot, project?.id])

  // Every write that can move an overage re-fetches alerts — that is the refresh
  // signal. Only an "exceeded" alert names milestones, so only then are they read.
  const namesMilestones = !!alerts?.alerts?.some(
    (alert) => AMOUNT_FIELD[`${alert.rule}:${alert.basis}`]
  )
  useEffect(() => {
    if (showFinanceSnapshot && project?.id && namesMilestones) {
      dispatch(fetchAlertContributingMilestones(project.id))
    }
  }, [dispatch, showFinanceSnapshot, project?.id, alerts, namesMilestones])

  // The number itself is informational only (spec: never clickable). Only
  // the warning icon is actionable, and it stays on this page — it reveals
  // the sidebar's Financial Alerts section and focuses this rule there,
  // rather than navigating to Finance.
  const focusAlert = (rule: string) => {
    dispatch(projectSetProp({ key: "showSnapshot", value: false }))
    dispatch(projectSetProp({ key: "financialAlertFocusRule", value: rule }))
  }

  const title = project?.lead_name || "No project name"
  const customer = project?.customer || project?.temporary_customer
  const typeDisplay =
    project?.project_type_details?.map(typeLabel).join(", ") || null
  const hasSnapshots = activeTab && snapshots?.[activeTab]

  const fullAddress = useMemo(() => {
    const parts = [
      project?.address,
      project?.city,
      project?.state,
      project?.zip_code
    ].filter(Boolean)

    return parts?.length > 0 ? parts.join(", ") : ""
  }, [project?.address, project?.city, project?.state, project?.zip_code])

  const toggleShowSnapshot = () => {
    dispatch(projectSetProp({ key: "showSnapshot", value: !showSnapshot }))
  }

  const handleStatusChange = (
    project_status: number,
    options?: StatusChangeOptions
  ) =>
    sendStatusChange(async (acknowledgement) => {
      await dispatch(
        updateProject({ id: project?.id, project_status, ...acknowledgement })
      ).unwrap()

      // The pill settles on the save; the history catches up behind it.
      if (canViewLogs) void dispatch(refreshProjectLogHistory(project?.id))
    }, options)

  useEffect(() => {
    dispatch(loadProjectsFilterOptions())
  }, [dispatch])

  // Dynamically computed on every render from the CURRENT snapshot/contract
  // figures — never a message string captured when the alert was raised —
  // so it can't go stale relative to what's on screen.
  const renderAlertTooltip = (
    rowKey: ContractRowKey,
    alert: AlertRow,
    contractValue: number
  ) => {
    const copy = ROW_COPY[rowKey]
    const figure = Number(
      (rowKey === "total_expenses"
        ? totalExpensesSnapshot?.[alert.basis]
        : salesPriceSnapshot?.[alert.basis]) ?? 0
    )
    const basisLabel =
      alert.basis === "planned" ? copy.plannedLabel : copy.actualLabel
    const difference = figure - contractValue
    const contributing = contributingCountByRow[rowKey] ?? 0

    return (
      <Flex vertical gap={2}>
        <Text className="text-white text-xs font-semibold">
          {RULE_TITLE[alert.rule] ?? alert.message}
        </Text>
        <Text className="text-white text-xs">
          {basisLabel}: {budgetFormat(figure)}
        </Text>
        <Text className="text-white text-xs">
          {copy.contractLabel}: {budgetFormat(contractValue)}
        </Text>
        {difference > 0 && (
          <Text className="text-white text-xs">
            Exceeds by: {budgetFormat(difference)}
          </Text>
        )}
        {difference < 0 && (
          <Text className="text-white text-xs">
            Under contract by: {budgetFormat(Math.abs(difference))}
          </Text>
        )}
        {contributing > 0 && (
          <Text className="text-white text-xs">
            {contributing} contributing{" "}
            {contributing === 1 ? "milestone" : "milestones"}
          </Text>
        )}
      </Flex>
    )
  }

  const renderContractFigure = (
    rowKey: ContractRowKey,
    label: string,
    value: number | null | undefined,
    alert: AlertRow | undefined,
    side: ContractBreakdownSide | null | undefined
  ) => {
    const original = Number(side?.original ?? value ?? 0)
    const changeRequests = Number(side?.included_change_requests ?? 0)
    // The headline figure is the CURRENT contract (original + included CR
    // impact), never the original baseline alone — `side.current` is the
    // backend's own computation of that, so this never re-derives it.
    const current = side ? Number(side.current) : Number(value ?? 0)

    return (
      <Flex
        vertical
        gap={0}
        className="select-none min-w-fit shrink-0"
        data-figure={
          rowKey === "sales_price" ? "contract_value" : "contract_expenses"
        }
      >
        <Flex align="center" gap={4} className="shrink-0">
          <Text type="secondary" className="text-xs whitespace-nowrap">
            {label}
          </Text>
          {alert && (
            <Tooltip title={renderAlertTooltip(rowKey, alert, current)}>
              <WarningOutlined
                className={`cursor-pointer ${
                  alert.severity === "critical"
                    ? "text-red-500"
                    : "text-orange-400"
                }`}
                onClick={() => focusAlert(alert.rule)}
              />
            </Tooltip>
          )}
        </Flex>
        <Text strong className="text-xl leading-tight whitespace-nowrap">
          {budgetFormat(current)}
        </Text>
        <Text type="secondary" className="text-xs whitespace-nowrap">
          {contractCaption(original, current, changeRequests)}
        </Text>
      </Flex>
    )
  }

  // Read-only: an import keeps its old CRM's figures with no caption, any other
  // project's come from the two contract figures beside them.
  const expectedCaption =
    expectedMarginSource === "contract"
      ? t("projectHeaderFigures.currentContract")
      : null

  const renderExpectedFigure = (
    key: string,
    label: string,
    display: string,
    negative: boolean
  ) => (
    <Flex
      vertical
      gap={0}
      className="select-none min-w-fit shrink-0"
      data-figure={key}
    >
      <Text type="secondary" className="text-xs whitespace-nowrap">
        {label}
      </Text>
      <Text
        strong
        className="text-xl leading-tight whitespace-nowrap"
        style={negative ? { color: semantic.errorText } : undefined}
      >
        {display}
      </Text>
      {expectedCaption && (
        <Text type="secondary" className="text-xs whitespace-nowrap">
          {expectedCaption}
        </Text>
      )}
    </Flex>
  )

  const grossProfit =
    expectedGrossProfit === null || expectedGrossProfit === undefined
      ? null
      : Number(expectedGrossProfit)

  const marginPercent = marginAtTwoDecimals(expectedMarginPercent)

  return (
    <Flex vertical gap={"small"}>
      <Flex justify={"space-between"} align={"center"} gap={"small"}>
        <Flex gap={"middle"} align={"center"} className="min-w-0">
          <Tooltip title="Back">
            <Button
              aria-label="Back"
              icon={<ArrowLeftOutlined />}
              size={"large"}
              onClick={handleBack}
            />
          </Tooltip>
          <RecordRef
            uid={project?.uid}
            type={typeDisplay}
            className="mb-1 min-w-0"
          />
        </Flex>

        <Flex gap={"small"}>
          {project?.id && (
            <AiPlanButton
              record={{
                id: project.id,
                kind: "project",
                name: project.lead_name ?? ""
              }}
            />
          )}
          {project?.id && (
            <RecordHeaderSendActions type="project" subject={project} />
          )}
          {/*<Button disabled icon={<Icon type={"Upload"} />}>*/}
          {/*  Import Template*/}
          {/*</Button>*/}
          {/*<Button disabled icon={<Icon type={"Download"} />}>*/}
          {/*  Export*/}
          {/*</Button>*/}
          {canEditProject && project?.id && (
            <Link to={RoutesPaths.project.edit(project.id)}>
              <Button icon={<Icon type={"Edit"} />}>
                {t("projectActions.editProject")}
              </Button>
            </Link>
          )}
          {showSnapshotToggle && (
            <Button
              className={"max-xl:hidden"}
              disabled={!hasSnapshots}
              icon={showSnapshot ? <LeftOutlined /> : <RightOutlined />}
              onClick={toggleShowSnapshot}
            />
          )}
        </Flex>
      </Flex>

      <Flex justify={"flex-start"} align={"flex-start"} gap={48}>
        {/* EAD-1292: min-w-0 with no floor let this column compress
            arbitrarily far under any sibling pressure — a plain title-ellipsis
            guard on the Title element doesn't help the address/customer row
            below it, which has no ellipsis of its own and just wraps
            character-by-character once its box gets narrow enough. A real
            floor keeps that row readable; shrink still lets it give width
            back above the floor. The basis is 560px now that the header spans
            the page above the snapshot panel, so a typical project name fits
            on a laptop instead of ending in an ellipsis. */}
        <Flex vertical className="min-w-[260px] shrink basis-[560px]">
          <Flex align={"center"} gap={"small"} className="min-w-0">
            <Title
              ellipsis={{ tooltip: title }}
              level={4}
              className={"m-0 min-w-0"}
            >
              {title}
            </Title>

            {fetchingOptions && (
              <Skeleton.Node className={"flex-1 h-7 rounded-2xl"} />
            )}

            {!fetchingOptions && (
              <>
                {canChangeStatus ? (
                  <ProjectStatusSelector
                    status={project?.project_status}
                    statuses={options?.project_status || []}
                    canClose={canClose}
                    onChange={handleStatusChange}
                  />
                ) : (
                  <ProjectStatusTag status={project?.project_status} />
                )}
                {project?.imported_from ? (
                  <Tag className="m-0">{t("projectsCommand.importedTag")}</Tag>
                ) : null}
                {project?.id && (
                  <ProjectStatusHistory
                    projectId={project.id}
                    canChangeStatus={canChangeStatus}
                    canViewLogs={canViewLogs}
                  />
                )}
              </>
            )}
          </Flex>
          <Flex wrap className={"gap-x-2"}>
            {fullAddress && <Text type={"secondary"}>{fullAddress}</Text>}
            {customer && (
              <Text type={"secondary"}>
                Customer: <Text strong>{getEntityName(customer)}</Text>
              </Text>
            )}
          </Flex>
          {project?.id && (
            <ProjectStatusSuggestions
              project={project}
              canChangeStatus={canChangeStatus}
              canClose={canClose}
              canViewLogs={canViewLogs}
            />
          )}
        </Flex>

        {showFinanceSnapshot && (
          <Flex gap={32} className="max-xl:hidden shrink-0">
            {renderContractFigure(
              "sales_price",
              "Contract Value",
              project?.sales_price,
              alertByRow.sales_price,
              contractBreakdown?.receivable
            )}
            <Divider type="vertical" className="!h-auto self-stretch" />
            {renderContractFigure(
              "total_expenses",
              "Contract Expenses",
              project?.planned_payable,
              alertByRow.total_expenses,
              contractBreakdown?.expenses
            )}
            <Divider type="vertical" className="!h-auto self-stretch" />
            {renderExpectedFigure(
              "expected_gross_profit",
              t("projectHeaderFigures.expectedGrossProfit"),
              grossProfit === null || isNaN(grossProfit)
                ? NO_FIGURE
                : accountingFormat(grossProfit),
              (grossProfit ?? 0) < 0
            )}
            <Divider type="vertical" className="!h-auto self-stretch" />
            {renderExpectedFigure(
              "expected_margin",
              t("projectHeaderFigures.expectedMargin"),
              marginPercent === null
                ? NO_FIGURE
                : `${marginPercent.toFixed(2)}%`,
              (marginPercent ?? 0) < 0
            )}
          </Flex>
        )}
      </Flex>
    </Flex>
  )
}
